import { useState } from 'react';
import type React from 'react';

import { useGetEvent } from '@vlabs/api/luna-api/client';
import type { EventId, Similarity } from '@vlabs/api/luna-api/model';
import { ButtonSwitch, ButtonSwitchGroup } from '@vlabs/uikit';
import type { WithClassNameProp, WithDataTestid } from '@vlabs/uikit/types';
import cn from 'classnames';

import {
  AdditionalPropertiesBySampleId,
  EventBodyAttributes,
  EventFaceAttributes,
  TopMatchEventInfo,
} from '@vlabs/monokit/entities/event-details';
import { GoToEventDetailsButton } from '@vlabs/monokit/features/go-to-event-details';
import { FindSimilarDropdown } from '@vlabs/monokit/features/point/find-similar';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { useTopMatchEventTranslation } from './i18n';
import st from './TopMatchEvent.module.sass';

type Tab = 'attributes' | 'additionalProperties';

interface TopMatchEventProps extends WithDataTestid, WithClassNameProp {
  eventId: EventId;
  similarity: Similarity;
}

const displayName = 'TopMatchEvent';
const testid = buildTestid(displayName);

interface AttributesTabProps extends WithDataTestid {
  eventId: EventId;
}

function AttributesTab({
  eventId,
  'data-testid': dataTestId = undefined,
}: AttributesTabProps): React.JSX.Element {
  return (
    <div className={st.AttributeGroups}>
      <EventFaceAttributes
        className={st.Attributes}
        eventId={eventId}
        {...testid(dataTestId)}
      />
      <EventBodyAttributes
        className={st.Attributes}
        eventId={eventId}
        {...testid(dataTestId)}
      />
    </div>
  );
}

interface AdditionalPropertiesTabProps extends WithDataTestid {
  eventId: EventId;
}

function AdditionalPropertiesTab({
  eventId,
  'data-testid': dataTestId = undefined,
}: AdditionalPropertiesTabProps): React.JSX.Element {
  const { t } = useTopMatchEventTranslation();
  const { data: event } = useGetEvent(eventId, { target: ['face_detections'] });
  const sampleId = event?.face_detections?.[0]?.sample_id ?? undefined;

  if (!sampleId) return <span className={st.Empty}>{t('no_data')}</span>;

  return (
    <AdditionalPropertiesBySampleId
      className={st.AdditionalProperties}
      sampleId={sampleId}
      {...testid(dataTestId)}
    />
  );
}

function TopMatchEvent({
  eventId,
  similarity,
  className = undefined,
  'data-testid': dataTestId = undefined,
}: TopMatchEventProps): React.JSX.Element {
  const { t } = useTopMatchEventTranslation();
  const [tab, setTab] = useState<Tab>('attributes');

  return (
    <div
      className={cn(st.Root, className)}
      {...testid(dataTestId)}
    >
      <h5>{t('title')}</h5>
      <div className={st.Panel}>
        <div className={st.Header}>
          <ButtonSwitchGroup
            onChange={setTab}
            size="small"
            value={tab}
            variant="square"
            {...testid(dataTestId)}
          >
            <ButtonSwitch value="attributes">
              {t('attributes')}
            </ButtonSwitch>
            <ButtonSwitch value="additionalProperties">
              {t('additional_properties')}
            </ButtonSwitch>
          </ButtonSwitchGroup>

          <div className={st.Actions}>
            <GoToEventDetailsButton
              eventId={eventId}
              variant="filled"
              {...testid(dataTestId)}
            />
            <FindSimilarDropdown
              eventId={eventId}
              type="top match"
              {...testid(dataTestId)}
            />
          </div>
        </div>

        <div className={st.Content}>
          <TopMatchEventInfo
            eventId={eventId}
            similarity={similarity}
            {...testid(dataTestId)}
          />
          {tab === 'attributes' && (
            <AttributesTab
              eventId={eventId}
              {...testid(dataTestId)}
            />
          )}
          {tab === 'additionalProperties' && (
            <AdditionalPropertiesTab
              eventId={eventId}
              {...testid(dataTestId)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

TopMatchEvent.displayName = displayName;

export { TopMatchEvent };
